<!-- UI-5 (5f): viewer Pipelines flyout — the single pipeline surface (the bottom
     AppBottomDrawer is removed). A vertical, click-to-configure catalog (no horizontal
     scroll); clicking a pipeline opens PipelineRunModal to set inputs + run. Recent runs
     update immediately after a run and are polled to completion (annotation-layers refresh
     on geometry output). Real data only (Task.fetchAll + TaskRun.fetchByProject). -->
<template>
  <div class="pipelines-panel">
    <!-- Catalog -->
    <h4 class="pp-section">{{ $t('app-engine.execute-a-task') }}</h4>
    <div v-if="loading" class="pp-state"><i class="fas fa-circle-notch fa-spin"></i></div>
    <!-- "Nothing to run" must account for the inference pipeline, which is not an App
         Engine task and so is not in `pipelines`. Testing only pipelines.length hid the
         whole list the moment the superseded tbsputum task was filtered out of it. -->
    <div v-else-if="!hasAnythingToRun" class="pp-state"><em>{{ $t('no-result') }}</em></div>
    <ul v-else class="pp-list">
      <!-- In-cluster inference pipeline. Not an App Engine task, so it is not in
           Task.fetchAll(); it is prepended here so it appears alongside them. -->
      <li v-for="ip in inferencePipelines" :key="ip.key">
        <button class="pp-item" :disabled="inferenceBusy" @click="runInference(ip)">
          <span class="pp-item-main">
            <span class="pp-name">{{ $t(ip.labelKey) }}</span>
            <span class="pp-sub">{{ ip.model || ip.key }}</span>
          </span>
          <span class="pp-item-status">
            <span class="pp-idle" :class="inferenceStatusClass">
              <span class="status-dot" :class="inferenceDotClass"></span>{{ inferenceStatusLabel }}
            </span>
            <span v-if="inferenceEtaLabel" class="pp-time">{{ inferenceEtaLabel }}</span>
          </span>
          <i class="fas fa-chevron-right pp-go"></i>
        </button>
        <div v-if="activeInferenceRun" class="pp-progress">
          <progress
            class="progress is-small"
            :class="activeInferenceRun.status === 'DELETING' ? 'is-danger' : 'is-info'"
            :value="inferencePercent === null ? undefined : inferencePercent"
            max="100"
          >{{ inferencePercent }}</progress>
          <span class="pp-progress-note">
            <template v-if="activeInferenceRun.status === 'DELETING'">
              <template v-if="activeInferenceRun.delete_total">
                {{ $t('pipelines-deleting-progress',
                      {done: activeInferenceRun.deleted_count, total: activeInferenceRun.delete_total}) }}
              </template>
              <template v-else>{{ $t('pipelines-deleting') }}</template>
            </template>
            <template v-else-if="inferenceIsIngesting">
              <template v-if="activeInferenceRun.total">
                {{ $t('pipelines-annotations-progress',
                      {done: activeInferenceRun.inserted, total: activeInferenceRun.total}) }}
              </template>
            </template>
            <template v-else-if="activeInferenceRun.tiles_total">
              {{ $t('pipelines-tiles', {done: activeInferenceRun.tiles_done, total: activeInferenceRun.tiles_total}) }}
            </template>
            <a v-if="activeInferenceRun.has_mask" class="pp-mask"
               @click.stop="downloadMask(activeInferenceRun)">{{ $t('pipelines-mask-download') }}</a>
            <a v-if="activeInferenceRun.status !== 'DELETING'" class="pp-stop"
               @click.stop="stopInference(activeInferenceRun)">{{ $t('pipelines-stop') }}</a>
          </span>
        </div>
      </li>
      <li v-for="p in pipelines" :key="p.key">
        <button class="pp-item" @click="openTask(p.task)">
          <span class="pp-item-main">
            <span class="pp-name">{{ pipelineDisplayName(p.task) }}</span>
            <span class="pp-sub">{{ p.sub }}</span>
          </span>
          <span class="pp-item-status">
            <task-run-status-dot v-if="p.state" :state="p.state" />
            <span v-else class="pp-idle"><span class="status-dot idle"></span>{{ $t('app-engine.task-run.not-run') }}</span>
            <span v-if="p.lastRunLabel" class="pp-time">{{ p.lastRunLabel }}</span>
          </span>
          <i class="fas fa-chevron-right pp-go"></i>
        </button>
      </li>
    </ul>

    <!-- Recent runs — inference runs and App Engine runs together, newest concern first -->
    <div class="pp-recent">
      <h4 class="pp-section">{{ $t('app-engine.recent-runs') }}</h4>


      <ul v-if="finishedInferenceRuns.length" class="pp-runs">
        <li v-for="r in finishedInferenceRuns" :key="r.run_uuid">
          <span class="pp-run-main">
            <span class="status-dot" :class="runDotClass(r.status)"></span>
            <span class="pp-run-name">{{ $t(labelFor(r.pipeline)) }}</span>
            <span class="pp-run-status" :class="runTextClass(r.status)">&nbsp;{{ r.status }}</span>
            <span class="pp-run-meta">
              {{ r.inserted }} &middot; {{ Math.round((r.insertion_threshold || 0) * 100) }}%
            </span>
          </span>
          <button v-if="r.has_mask" class="rr-mask" :title="$t('pipelines-mask-help')"
                  @click="downloadMask(r)">
            <i class="fas fa-download"></i>
          </button>
          <button class="rr-del" :title="$t('delete')" @click="deleteInference(r)">
            <i class="fas fa-trash"></i>
          </button>
        </li>
      </ul>

      <recent-runs-list v-if="allTaskRuns.length" :task-runs="allTaskRuns" :can-delete="canManageProject" @delete="handleDeleteRun" />
      <div v-if="!allTaskRuns.length && !finishedInferenceRuns.length" class="pp-state pp-state-sm">
        <em>{{ $t('no-result') }}</em>
      </div>
    </div>

    <!-- Existing results: re-draw, or re-run the model -->
    <b-modal :active.sync="existingModal" has-modal-card trap-focus aria-role="dialog">
      <div class="modal-card existing-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ $t('pipelines-existing-title') }}</p>
        </header>
        <section class="modal-card-body">
          <p>{{ $t('pipelines-existing-body') }}</p>
          <p v-if="existingWhen" class="existing-when">
            {{ $t('pipelines-existing-when', {when: existingWhen}) }}
          </p>
          <p class="existing-note">{{ $t('pipelines-existing-replace-note') }}</p>
        </section>
        <footer class="modal-card-foot existing-foot">
          <button class="button" @click="existingModal = false">{{ $t('button-cancel') }}</button>
          <button class="button" @click="launchInference(false)">
            {{ $t('pipelines-existing-rerun') }}
          </button>
          <button class="button is-link" @click="launchInference(true)">
            {{ $t('pipelines-existing-reuse') }}
          </button>
        </footer>
      </div>
    </b-modal>

    <!-- Configure + run popup -->
    <b-modal :active.sync="showModal" has-modal-card trap-focus :destroy-on-hide="true" aria-role="dialog">
      <pipeline-run-modal
        v-if="selectedTask"
        :task="selectedTask"
        :project-id="project.id"
        :image-id="image.id"
        @ran="onRan"
        @close="showModal = false"
      />
    </b-modal>
  </div>
</template>

<script>
import {get} from '@/utils/store-helpers';
import Task from '@/utils/appengine/task';
import TaskRun from '@/utils/appengine/task-run';
import TaskRunStatusDot from '@/components/appengine/task-run/TaskRunStatusDot';
import RecentRunsList from '@/components/viewer/panels/RecentRunsList';
import PipelineRunModal from '@/components/viewer/panels/PipelineRunModal';
import pipelineService from '@/api/pipeline-service';
import {pipelineDisplayName, isAnnotationImportPipeline} from '@/utils/appengine/pipeline-display';

const MAX_RUN_LOOKUPS = 15;

// The in-cluster RF-DETR inference pipeline. Unlike everything else in this panel it is
// NOT an App Engine task — it is a queued job run by a single-replica worker — so it has
// no Task record, does not come back from Task.fetchAll(), and its runs do not appear in
// TaskRun.fetchByProject(). It is sourced separately from ims-service and merged in here.
// Which pipelines are in-cluster inference, and which App Engine task each supersedes,
// come from the backend catalog (GET /pipelines/catalog) rather than being listed here.
// One registry, rendered by every surface that lists pipelines.

// Its App Engine predecessor. Hidden from this panel because the inference pipeline
// replaces it in the UI; the task itself stays registered and callable.
const SUPERSEDED_BY_INFERENCE = 'ai.zaya.pipeline.tbsputum';

// Task-run polling. This used to be a flat setInterval(…, 2000) over every non-terminal run,
// which never stopped: three runs on the satvi project have been stuck in PROVISIONED since
// 30 Aug, and a single idle viewer was re-fetching them 137 times in 8 minutes (0.36 req/s of
// pure polling, per open viewer, straight through Core to Postgres). At a thousand viewers that
// is ~360 req/s before anyone does any work — above the tenant's own API rate limit.
const POLL_MIN_MS = 2000;          // while something is actually moving
const POLL_MAX_MS = 30000;         // once nothing has changed for a while
// A run that has sat in a non-terminal state this long is not going to change while this viewer
// watches it. Stop asking; the panel still shows its last known state.
const POLL_STALE_AFTER_MS = 60 * 60 * 1000;

const INFERENCE_ACTIVE_STATES = [
  'QUEUED', 'INFERRING', 'INGESTING', 'INGEST_RUNNING', 'REINGEST', 'DELETING',
];

// Zaya annotation-import pipelines: their pkl input is auto-resolved server-side by image name
// (ims-service launcher), so we skip the native "attach a file" modal for these tasks.
const ZAYA_AUTO_PIPELINES = {
  'ai.zaya.pipeline.tbsputum': 'tb-sputum',
  'ai.zaya.pipeline.tbtissue': 'tb-tissue',
  'ai.zaya.pipeline.uc': 'uc',
};

// Pipelines whose result is a precomputed segmentation pyramid (a raster overlay served via
// /v1/ims/segmentation/{imageId}), NOT vector annotations — currently UC only. TB Tissue produces
// classic vector annotations (same as TB Sputum), so it intentionally stays OUT of this set.
const SEGMENTATION_PIPELINES = new Set(['ai.zaya.pipeline.uc']);

// Per-pipeline run-confirmation message; falls back to the generic key for any other auto pipeline.
const AUTO_PIPELINE_CONFIRM_KEY = {
  'ai.zaya.pipeline.uc': 'pipelines-auto-run-confirm-uc',
  'ai.zaya.pipeline.tbsputum': 'pipelines-auto-run-confirm-tb',
  'ai.zaya.pipeline.tbtissue': 'pipelines-auto-run-confirm-tb-tissue',
};

export default {
  name: 'pipelines-panel',
  components: {TaskRunStatusDot, RecentRunsList, PipelineRunModal},
  props: {
    index: String
  },
  data() {
    return {
      loading: true,
      tasks: [],
      latestByTask: {},
      allTaskRuns: [],
      trackedTaskRuns: [],
      showModal: false,
      selectedTask: null,
      pollTimer: null,
      pollDelay: POLL_MIN_MS,
      inferenceRuns: [],
      inferencePipelines: [],
      inferenceBusy: false,
      inferencePoll: null,
      existingModal: false,
      existingWhen: '',
      selectedInference: null,
    };
  },
  computed: {
    project: get('currentProject/project'),
    canManageProject() {
      return this.$store.getters['currentProject/canManageProject'];
    },
    image() {
      return this.$store.getters['currentProject/currentViewer'].images[this.index].imageInstance;
    },
    // The panel is rendered with v-show, so it stays mounted — and polling — even when closed.
    // Historical runs only drive the status dot, which nobody can see while it is closed.
    panelVisible() {
      return this.$store.getters['currentProject/currentViewer']
        .images[this.index].activePanel === 'pipelines';
    },
    hasAnythingToRun() {
      return this.inferencePipelines.length > 0 || this.pipelines.length > 0;
    },
    inferenceRunsForImage() {
      return this.inferenceRuns.filter(r => r.image === this.image.id);
    },
    activeInferenceRun() {
      return this.inferenceRunsForImage.find(r => INFERENCE_ACTIVE_STATES.includes(r.status)) || null;
    },
    finishedInferenceRuns() {
      return this.inferenceRunsForImage.filter(r => !INFERENCE_ACTIVE_STATES.includes(r.status));
    },
    inferenceIsIngesting() {
      let r = this.activeInferenceRun;
      return !!r && ['INGESTING', 'INGEST_RUNNING', 'REINGEST'].includes(r.status);
    },
    inferencePercent() {
      // null renders an indeterminate bar: tissue detection and the ingest phase have no
      // tile count, and 0% there reads as stuck rather than working.
      let row = this.activeInferenceRun;
      if (!row) {
        return null;
      }
      // Two phases, two units. Inference counts tiles; ingest counts annotations — and a
      // re-draw from stored results skips inference entirely, so keying only off tiles
      // left that case with a bar that spun forever. null still means indeterminate, for
      // the genuinely unmeasurable stretches (tissue detection, and ingest before the
      // first chunk lands).
      if (row.status === 'DELETING') {
        return row.delete_total
          ? Math.min(100, Math.round((row.deleted_count / row.delete_total) * 100))
          : null;
      }
      let ingesting = ['INGESTING', 'INGEST_RUNNING', 'REINGEST'].includes(row.status);
      if (ingesting) {
        return row.total ? Math.min(100, Math.round((row.inserted / row.total) * 100)) : null;
      }
      if (!row.tiles_total) {
        return null;
      }
      return Math.min(100, Math.round((row.tiles_done / row.tiles_total) * 100));
    },
    latestInferenceRun() {
      // Newest-first from the API, so entry 0 is this pipeline's current state here.
      return this.inferenceRunsForImage[0] || null;
    },
    inferenceStatusLabel() {
      if (this.activeInferenceRun) {
        return this.inferencePhaseLabel;
      }
      let r = this.latestInferenceRun;
      // Reporting "Not run yet" on a pipeline that has already finished was the single
      // most misleading thing in this panel.
      return r ? r.status : this.$t('app-engine.task-run.not-run');
    },
    inferenceDotClass() {
      return this.activeInferenceRun
        ? 'running'
        : this.runDotClass(this.latestInferenceRun && this.latestInferenceRun.status);
    },
    inferenceStatusClass() {
      return this.activeInferenceRun
        ? ''
        : this.runTextClass(this.latestInferenceRun && this.latestInferenceRun.status);
    },
    inferencePhaseLabel() {
      let r = this.activeInferenceRun;
      if (!r) {
        return '';
      }
      let key = `pipelines-phase-${(r.phase || r.status).toLowerCase()}`;
      let label = this.$t(key);
      return label === key ? r.status : label;
    },
    inferenceEtaLabel() {
      let r = this.activeInferenceRun;
      if (!r || r.status === 'QUEUED') {
        return '';
      }
      if (r.eta_seconds === null || r.eta_seconds === undefined) {
        return r.status === 'INFERRING' ? this.$t('pipelines-eta-calculating') : '';
      }
      return this.$t('pipelines-eta', {time: this.humanDuration(r.eta_seconds)});
    },
    pipelines() {
      return this.tasks
        .filter(task => task.namespace !== SUPERSEDED_BY_INFERENCE || !this.inferencePipelines.length)
        .map(task => {
          let key = `${task.namespace}/${task.version}`;
          let latest = this.latestByTask[key];
          return {
            key,
            task,
            sub: [task.namespace, task.version].filter(Boolean).join(' · '),
            state: latest ? latest.state : null,
            lastRunLabel: latest ? new Date(latest.createdAt).toLocaleString() : '',
          };
        });
    }
  },
  methods: {
    pipelineDisplayName,
    async downloadMask(row) {
      try {
        let {data} = await pipelineService.downloadTissueMask(row.run_uuid);
        let name = (row.slide_name || 'slide').replace(/\.[^.]+$/, '') + '_tissue_mask.png';
        let url = window.URL.createObjectURL(new Blob([data], {type: 'image/png'}));
        let a = document.createElement('a');
        a.href = url;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.log(error);
        this.$notify({type: 'is-warning', text: this.$t('pipelines-mask-unavailable')});
      }
    },
    labelFor(key) {
      // Never fall back to the raw key: it is an internal identifier that contains
      // wording we deliberately do not show clients.
      let p = this.inferencePipelines.find(x => x.key === key);
      return p ? p.labelKey : 'pipeline-unknown';
    },
    runDotClass(status) {
      switch (status) {
        case 'FINISHED': return 'ok';
        case 'FAILED':   return 'bad';
        case 'STOPPED':  return 'warn';
        case undefined:
        case null:       return 'idle';
        default:         return 'running';
      }
    },
    runTextClass(status) {
      switch (status) {
        case 'FINISHED': return 'txt-ok';
        case 'FAILED':   return 'txt-bad';
        case 'STOPPED':  return 'txt-warn';
        default:         return '';
      }
    },
    humanDuration(seconds) {
      let sec = Math.max(0, Math.round(seconds));
      if (sec < 90) {
        return `${sec}s`;
      }
      let min = Math.round(sec / 60);
      if (min < 90) {
        return `${min} min`;
      }
      let h = Math.floor(min / 60);
      let rem = min % 60;
      return rem ? `${h}h ${rem}m` : `${h}h`;
    },
    async fetchCatalog() {
      try {
        let {data} = await pipelineService.getCatalog();
        this.inferencePipelines = (data.pipelines || []).filter(p => p.kind === 'inference');
      } catch (error) {
        console.log(error);
        this.inferencePipelines = [];
      }
    },
    async fetchInferenceRuns() {
      try {
        let {data} = await pipelineService.listRuns({project: this.project.id});
        let keys = this.inferencePipelines.map(p => p.key);
        this.inferenceRuns = (data.runs || []).filter(r => keys.includes(r.pipeline));
      } catch (error) {
        // ims-service without the inference pipeline deployed: hide the entry rather than
        // offering a button that cannot work.
        console.log(error);
        this.inferenceRuns = [];
      }
      this.ensureInferencePolling();
    },
    ensureInferencePolling() {
      if (this.activeInferenceRun && !this.inferencePoll) {
        this.inferencePoll = setInterval(() => this.fetchInferenceRuns(), 5000);
      } else if (!this.activeInferenceRun && this.inferencePoll) {
        clearInterval(this.inferencePoll);
        this.inferencePoll = null;
        // A run just finished. Same refresh the App Engine finish path uses: the pipeline
        // draws into bk_* confidence-bucket terms, which may have no layer yet, so the
        // layer set has to be re-read — reloadAnnotations alone would not surface them.
        this.$eventBus.$emit('annotation-layers:refresh');
        this.$eventBus.$emit('reloadAnnotations', {idImage: this.image.id});
      }
    },
    async runInference(ip) {
      this.selectedInference = ip;
      if (this.activeInferenceRun) {
        this.$notify({type: 'is-warning', text: this.$t('pipelines-auto-run-skipped', {reason: 'already running'})});
        return;
      }
      // If the detector has already been over this slide, offer to re-draw from the
      // stored results rather than spending hours repeating the work.
      try {
        let {data} = await pipelineService.availableResults(ip.key, this.image.id);
        if (data && data.available) {
          this.existingWhen = data.producedAt ? new Date(data.producedAt).toLocaleString() : '';
          this.existingModal = true;
          return;
        }
      } catch (error) {
        console.log(error);   // no probe: fall through to an ordinary run
      }
      this.confirmAndRun();
    },
    confirmAndRun() {
      this.$buefy.dialog.confirm({
        title: this.$t((this.selectedInference || {}).labelKey || 'pipelines'),
        message: this.$t('pipelines-auto-run-confirm-tb'),
        confirmText: this.$t('pipelines-run-button'),
        cancelText: this.$t('button-cancel'),
        onConfirm: () => this.launchInference(false),
      });
    },
    async launchInference(reuseResults) {
      this.existingModal = false;
      this.inferenceBusy = true;
      try {
        let {data} = await pipelineService.runPipeline({
          pipeline: (this.selectedInference || {}).key,
          project: this.project.id,
          images: [this.image.id],
          reuseResults,
        });
        let result = (data.runs || [])[0] || {};
        if (result.status === 'reusing') {
          this.$notify({type: 'success', text: this.$t('pipelines-reusing')});
        } else if (result.status === 'queued' || result.status === 'running') {
          this.$notify({type: 'success', text: this.$t('pipelines-auto-run-started')});
        } else {
          this.$notify({
            type: 'is-warning',
            text: this.$t('pipelines-auto-run-skipped', {reason: result.reason || result.status}),
          });
        }
        await this.fetchInferenceRuns();
      } catch (error) {
        console.log(error);
        this.$notify({type: 'error', text: this.$t('pipelines-auto-run-error')});
      } finally {
        this.inferenceBusy = false;
      }
    },
    stopInference(run) {
      this.$buefy.dialog.confirm({
        title: this.$t('pipelines-stop'),
        message: this.$t('pipelines-stop-confirm'),
        type: 'is-warning',
        confirmText: this.$t('pipelines-stop'),
        cancelText: this.$t('button-cancel'),
        onConfirm: async () => {
          try {
            await pipelineService.stopRun(run.run_uuid);
            this.$notify({type: 'success', text: this.$t('pipelines-stopped')});
            await this.fetchInferenceRuns();
          } catch (error) {
            console.log(error);
            this.$notify({type: 'error', text: this.$t('pipelines-auto-run-error')});
          }
        },
      });
    },
    deleteInference(run) {
      this.$buefy.dialog.confirm({
        title: this.$t('confirm-deletion'),
        message: this.$t('pipelines-delete-confirm'),
        type: 'is-danger',
        confirmText: this.$t('button-confirm'),
        cancelText: this.$t('button-cancel'),
        onConfirm: async () => {
          try {
            let {data} = await pipelineService.deleteRun(run.run_uuid);
            if (data && data.jobId) {
              await this.pollDeletion(data.jobId);
            }
            await this.fetchInferenceRuns();
            this.$eventBus.$emit('annotation-layers:refresh');
            this.$eventBus.$emit('reloadAnnotations', {idImage: this.image.id});
          } catch (error) {
            console.log(error);
            this.$notify({type: 'error', text: this.$t('error-delete-run')});
          }
        },
      });
    },
    handleDeleteRun(run) {
      let isPipeline = isAnnotationImportPipeline(run.task);
      this.$buefy.dialog.confirm({
        title: this.$t('confirm-deletion'),
        message: isPipeline ? this.$t('confirm-deletion-run-annotations') : this.$t('confirm-deletion-run'),
        type: 'is-danger',
        confirmText: this.$t('button-confirm'),
        cancelText: this.$t('button-cancel'),
        onConfirm: async () => {
          try {
            // Our annotation-import pipelines: ims-service deletes the annotations + the
            // App Engine run + its core record in one call. Other app-engine runs use core's
            // own task-run DELETE.
            if (isPipeline) {
              let {data} = await pipelineService.deleteRun(run.id);
              // Async delete: the run + its annotations are removed by the ims-service worker. Poll
              // the job to completion so the run actually disappears here (and FAILED surfaces),
              // instead of returning while the deletion is still queued.
              if (data && data.jobId) {
                await this.pollDeletion(data.jobId);
              }
            } else {
              await run.delete();
            }
            await this.fetchTaskRuns();
            // UC run deleted → its segmentation pyramid is gone; tell the viewer to drop the overlay
            // + class menu live (mirror of the run-finished refresh), so no page reload is needed.
            if (run.task && SEGMENTATION_PIPELINES.has(run.task.namespace)) {
              this.$eventBus.$emit('segmentation:refresh', {imageId: this.image.id, expect: 'absent'});
            }
          } catch (error) {
            console.log(error);
            this.$notify({type: 'error', text: this.$t('error-delete-run')});
          }
        }
      });
    },
    async pollDeletion(jobId, tries = 150, intervalMs = 2000) {
      // Surfaces progress while polling — deleting 36,000 annotations takes ~27 s, and a
      // spinner with no numbers behind it is indistinguishable from a hang.
      // Poll an async deletion job until it leaves QUEUED/RUNNING. DONE -> resolve; FAILED/timeout
      // -> throw so the caller's catch shows the error toast. 150 x 2s = 5 min ceiling.
      for (let i = 0; i < tries; i++) {
        let {data} = await pipelineService.getDeletion(jobId);
        if (data.status === 'DONE') {
          return;
        }
        if (data.status === 'FAILED') {
          throw new Error(data.error || 'deletion failed');
        }
        await new Promise(resolve => setTimeout(resolve, intervalMs));
      }
      throw new Error('deletion timed out');
    },
    openTask(task) {
      // Zaya auto-import pipelines resolve their pkl server-side (by image name) — run directly,
      // no input-attach modal. All other App Engine tasks use the native provisioning modal.
      let autoPipeline = ZAYA_AUTO_PIPELINES[task.namespace];
      if (autoPipeline) {
        this.runAutoPipeline(task, autoPipeline);
        return;
      }
      this.selectedTask = task;
      this.showModal = true;
    },
    runAutoPipeline(task, pipeline) {
      this.$buefy.dialog.confirm({
        title: this.pipelineDisplayName(task),
        message: this.$t(AUTO_PIPELINE_CONFIRM_KEY[task.namespace] || 'pipelines-auto-run-confirm'),
        confirmText: this.$t('pipelines-run-button'),
        cancelText: this.$t('button-cancel'),
        onConfirm: async () => {
          try {
            let {data} = await pipelineService.runPipeline({
              pipeline,
              project: this.project.id,
              images: [this.image.id],
            });
            let result = (data.runs || [])[0] || {};
            if (result.status === 'running') {
              this.$notify({type: 'success', text: this.$t('pipelines-auto-run-started')});
              await this.fetchTaskRuns();
            } else {
              this.$notify({
                type: 'is-warning',
                text: this.$t('pipelines-auto-run-skipped', {reason: result.reason || result.status})
              });
            }
          } catch (error) {
            console.log(error);
            this.$notify({type: 'error', text: this.$t('pipelines-auto-run-error')});
          }
        }
      });
    },
    getTask(taskRun) {
      return this.tasks.find(task => task.id === taskRun.task.id);
    },
    async fetchTasks() {
      try {
        this.tasks = await Task.fetchAll();
      } catch (error) {
        console.log(error);
        this.tasks = [];
      }
    },
    async fetchTaskRuns() {
      try {
        let runs = await TaskRun.fetchByProject(this.project.id);
        // This panel is "Pipelines on THIS image" — only show runs for the current image.
        runs = runs.filter(r => r.image === this.image.id);
        runs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        let recent = runs.slice(0, MAX_RUN_LOOKUPS);
        let resolved = await Promise.all(recent.map(async ({project, taskRunId, createdAt, image}) => {
          try {
            let status = await Task.fetchTaskRunStatus(this.project.id, taskRunId);
            let tr = new TaskRun({...status, project});
            tr.image = image;
            if (!tr.createdAt) {
              tr.createdAt = createdAt;
            }
            return tr;
          } catch (e) {
            return null;
          }
        }));
        let list = resolved.filter(Boolean);
        this.allTaskRuns = list.slice(0, 5);

        // latest run state per task → catalog status dot
        let latest = {};
        list.forEach(tr => {
          let t = tr.task || {};
          let key = `${t.namespace}/${t.version}`;
          if (!latest[key]) {
            latest[key] = {state: tr.state, createdAt: tr.createdAt};
          }
        });
        this.latestByTask = latest;
      } catch (error) {
        console.log(error);
      }
    },
    onRan(taskRun) {
      // immediate recent-runs update + track to completion
      this.allTaskRuns = [taskRun, ...this.allTaskRuns].slice(0, 5);
      this.trackedTaskRuns = [taskRun, ...this.trackedTaskRuns].slice(0, 5);
      // The user just started this one: go back to the fast cadence immediately, whatever the
      // backoff had drifted to while the panel sat idle.
      this.resetPollCadence();
      if (taskRun.task) {
        this.$set(this.latestByTask, `${taskRun.task.namespace}/${taskRun.task.version}`, {
          state: taskRun.state,
          createdAt: taskRun.createdAt || new Date().toISOString(),
        });
      }
    },
    // Which runs are still worth asking about.
    //
    // Runs started from THIS viewer keep being polled even with the panel closed: finishing one
    // fires annotation-layers:refresh, and the user must get that whether or not they left the
    // panel open. Historical runs only drive the status dot, so they are polled only while the
    // panel is actually on screen.
    runsNeedingPoll() {
      const now = Date.now();
      const notStale = (tr) => {
        const created = tr.createdAt ? new Date(tr.createdAt).getTime() : NaN;
        return !Number.isFinite(created) || (now - created) < POLL_STALE_AFTER_MS;
      };
      const candidates = this.panelVisible
        ? [...this.trackedTaskRuns, ...this.allTaskRuns]
        : [...this.trackedTaskRuns];
      const seen = new Set();
      const pending = [];
      for (const tr of candidates) {
        if (!tr || seen.has(tr.id) || tr.isTerminalState() || !notStale(tr)) {
          continue;
        }
        seen.add(tr.id);
        pending.push(tr);
      }
      return pending;
    },
    schedulePoll(delay) {
      clearTimeout(this.pollTimer);
      this.pollTimer = setTimeout(() => this.pollTrackedRuns(), delay);
    },
    // Back to the fast cadence — a run was just started, or the panel was just opened.
    resetPollCadence() {
      this.pollDelay = POLL_MIN_MS;
      this.schedulePoll(POLL_MIN_MS);
    },
    async pollTrackedRuns() {
      // Self-scheduling rather than setInterval: the next delay depends on whether anything
      // actually moved, and an interval cannot back off.
      const pending = this.runsNeedingPoll();
      if (!pending.length) {
        this.pollDelay = POLL_MAX_MS;
        this.schedulePoll(POLL_MAX_MS);
        return;
      }
      let changed = false;
      for (let taskRun of pending) {
        const stateBefore = taskRun.state;
        try {
          await taskRun.fetch();
          if (taskRun.state !== stateBefore) {
            changed = true;
          }
          const idx = this.allTaskRuns.findIndex(r => r.id === taskRun.id);
          if (idx !== -1) {
            this.allTaskRuns.splice(idx, 1, taskRun);
          }
          // keep the catalog status dot live (not just the recent-runs list):
          // this tracked run is the latest for its task, so reflect its new state.
          if (taskRun.task) {
            this.$set(this.latestByTask, `${taskRun.task.namespace}/${taskRun.task.version}`, {
              state: taskRun.state,
              createdAt: taskRun.createdAt || (this.latestByTask[`${taskRun.task.namespace}/${taskRun.task.version}`] || {}).createdAt,
            });
          }
          let task = this.getTask(taskRun);
          if (taskRun.isTerminalState() && task) {
            if (task.hasGeometryOutput()) {
              await taskRun.fetchOutputs();
              this.$eventBus.$emit('annotation-layers:refresh');
            }
            // UC produces a precomputed segmentation pyramid (not vector annotations); tell the
            // viewer to reload its raster overlay + class menu so the user needn't refresh the page.
            if (SEGMENTATION_PIPELINES.has(task.namespace)) {
              this.$eventBus.$emit('segmentation:refresh', {imageId: this.image.id, expect: 'present'});
            }
          }
        } catch (e) {
          console.log(e);
        }
      }
      this.trackedTaskRuns = this.trackedTaskRuns.filter(tr => !tr.isTerminalState());
      // Responsive while a pipeline is genuinely progressing; quiet when it is not.
      this.pollDelay = changed ? POLL_MIN_MS : Math.min(this.pollDelay * 2, POLL_MAX_MS);
      this.schedulePoll(this.pollDelay);
    },
    async refresh() {
      this.loading = true;
      await this.fetchTasks();
      await this.fetchTaskRuns();
      this.loading = false;
    }
  },
  async created() {
    await this.refresh();
    await this.fetchCatalog();
    await this.fetchInferenceRuns();
    this.schedulePoll(POLL_MIN_MS);
  },
  watch: {
    // Opening the panel must show a live dot straight away, not after the backoff expires.
    panelVisible(open) {
      if (open) {
        this.resetPollCadence();
      }
    },
  },
  beforeDestroy() {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
    if (this.inferencePoll) {
      clearInterval(this.inferencePoll);
      this.inferencePoll = null;
    }
  }
};
</script>

<style scoped>
.existing-card {
  width: 34rem;
  max-width: 100%;
}

.existing-when {
  margin-top: 0.75rem;
  font-weight: 600;
}

.existing-note {
  margin-top: 0.75rem;
  font-size: 0.85rem;
  color: #7a7a7a;
}

.existing-foot {
  justify-content: flex-end;
}

.pp-progress {
  padding: 0 0.75rem 0.5rem;
}

.pp-progress .progress {
  margin-bottom: 0.2rem;
  height: 0.4rem;
}

.pp-progress-note {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #7a7a7a;
  font-variant-numeric: tabular-nums;
}

.pp-stop {
  color: #c46b02;
  cursor: pointer;
}

.pp-mask {
  color: #1e63d0;
  cursor: pointer;
  margin-right: 0.6rem;
}

.pp-runs .rr-mask {
  border: none;
  background: none;
  color: #b5b5b5;
  cursor: pointer;
  padding: 0 0.25rem;
}

.pp-runs .rr-mask:hover {
  color: #1e63d0;
}

.pp-runs {
  list-style: none;
  margin: 0;
  padding: 0;
}

.pp-runs li {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.3rem 0.75rem;
  border-top: 1px solid #f0f0f0;
}

.pp-run-status {
  font-weight: 600;
  margin-left: 0.4rem;
}

.pp-run-meta {
  margin-left: 0.5rem;
  color: #7a7a7a;
  font-variant-numeric: tabular-nums;
}

.pp-run-actions a {
  color: #d13438;
  cursor: pointer;
}

.status-dot.running { background: #3298dc; }
.status-dot.ok      { background: #23d160; }
.status-dot.bad     { background: #ff3860; }
.status-dot.warn    { background: #ffdd57; }

.txt-ok   { color: #257942; font-weight: 600; }
.txt-bad  { color: #cc0f35; font-weight: 600; }
.txt-warn { color: #946c00; font-weight: 600; }

.pp-run-name {
  margin-left: 0.4rem;
  font-weight: 600;
}

.pp-runs .rr-del {
  border: none;
  background: none;
  color: #b5b5b5;
  cursor: pointer;
  padding: 0 0.25rem;
}

.pp-runs .rr-del:hover {
  color: #ff3860;
}

.pipelines-panel {
  font-size: 12.5px;
  overflow-x: hidden;
}

.pp-section {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #8a91a1;
  margin: 0.25rem 0 0.5rem;
}

.pp-list {
  list-style: none;
  margin: 0 0 0.5rem;
  padding: 0;
}

.pp-list li {
  margin-bottom: 6px;
}

/* full-width clickable row — vertical layout, no horizontal scroll */
.pp-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  text-align: left;
  background: #fff;
  border: 1px solid #e5e8ee;
  border-radius: 10px;
  padding: 9px 11px;
  cursor: pointer;
  transition: border-color 0.12s, box-shadow 0.12s;
}

.pp-item:hover {
  border-color: #1e63d0;
  box-shadow: 0 2px 8px rgba(30, 99, 208, 0.12);
}

.pp-item-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.pp-name {
  font-weight: 600;
  color: #0e1116;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pp-sub {
  font-size: 11px;
  color: #5a6172;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pp-item-status {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  flex: 0 0 auto;
}

.pp-time {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 10px;
  color: #8a91a1;
}

.pp-idle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #8a91a1;
  white-space: nowrap;
  font-size: 11px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  flex: 0 0 8px;
}

.status-dot.idle {
  background: #8a91a1;
}

.pp-go {
  color: #c4cad6;
  font-size: 11px;
  flex: 0 0 auto;
}

.pp-recent {
  border-top: 1px solid #eef0f4;
  margin-top: 0.5rem;
  padding-top: 0.5rem;
}

.pp-state {
  padding: 1.25rem;
  text-align: center;
  color: #8a91a1;
}

.pp-state-sm {
  padding: 0.5rem;
  font-size: 11px;
}
</style>
